import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Email,
} from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    login: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [validationErrors, setValidationErrors] = useState({
    login: false,
    password: false,
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  
  const captchaRef = useRef<ReCAPTCHA>(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('sessionExpired') === 'true') {
      setSessionExpired(true);
      setError(t('errors.sessionExpired'));
      
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, t]);

  React.useEffect(() => {
    if (loginAttempts >= 1 && RECAPTCHA_SITE_KEY) {
      setShowCaptcha(true);
    }
  }, [loginAttempts, RECAPTCHA_SITE_KEY]);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/calendar';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: false,
      }));
    }
    
    if (error && !sessionExpired) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form fields
    const newValidationErrors = {
      login: !formData.login.trim(),
      password: !formData.password.trim(),
    };
    
    setValidationErrors(newValidationErrors);
    
    // If there are validation errors, don't proceed with login
    if (newValidationErrors.login || newValidationErrors.password) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      if (loginAttempts >= 1 && RECAPTCHA_SITE_KEY && !captchaToken) {
        if (captchaRef.current) {
          const token = await captchaRef.current.executeAsync();
          if (token) {
            setCaptchaToken(token);
          } else {
            setError(t('auth.invalidCredentials'));
            setIsSubmitting(false);
            return;
          }
        } else {
          setError(t('auth.invalidCredentials'));
          setIsSubmitting(false);
          return;
        }
      }

      await login(formData.login, formData.password, captchaToken || undefined);
      
      // Clear validation errors on successful login
      setValidationErrors({ login: false, password: false });
      setLoginAttempts(0);
      setCaptchaToken(null);
      setShowCaptcha(false);
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
      
      // Navigation will be handled by the useEffect above
    } catch (error) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      
      if (newAttempts >= 1 && RECAPTCHA_SITE_KEY) {
        setShowCaptcha(true);
      }
      
      setError(error instanceof Error ? error.message : t('auth.invalidCredentials'));
      
      setCaptchaToken(null);
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleContactAdministrator = () => {
    setContactDialogOpen(true);
  };

  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (error && error.includes(t('auth.invalidCredentials'))) {
      setError(null);
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 400,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              textAlign: 'center',
              py: 3,
            }}
          >
            <Typography variant="h4" component="h1" fontWeight="bold">
              {t('navigation.hairSalonCrm')}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
              {t('auth.signInToAccount')}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                id="login"
                label={t('auth.login')}
                name="login"
                autoComplete="username"
                autoFocus
                value={formData.login}
                onChange={handleInputChange}
                error={validationErrors.login}
                helperText={validationErrors.login ? t('forms.required') : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color={validationErrors.login ? "error" : "action"} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                error={validationErrors.password}
                helperText={validationErrors.password ? t('forms.required') : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color={validationErrors.password ? "error" : "action"} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              {/* CAPTCHA v3 - Invisible */}
              {showCaptcha && RECAPTCHA_SITE_KEY && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    onErrored={() => setCaptchaToken(null)}
                    theme="light"
                    size="invisible"
                    badge="inline"
                  />
                </Box>
              )}

              {/* Show message if CAPTCHA is required but not configured */}
              {showCaptcha && !RECAPTCHA_SITE_KEY && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mb: 2,
                  p: 2,
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: 1,
                  color: '#856404'
                }}>
                  <Typography variant="body2" textAlign="center">
                    {t('auth.captchaRequired')}<br/>
                    {t('auth.captchaContactMessage')}
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('auth.needHelp')}{' '}
                  <Button
                    variant="text"
                    onClick={handleContactAdministrator}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: 'primary.main',
                    }}
                  >
                    {t('auth.contactAdministrator')}
                  </Button>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.copyright')}
          </Typography>
        </Box>
      </Box>

      {/* Contact Administrator Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Email sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" component="div">
            {t('auth.contactAdministratorTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('auth.contactAdministratorMessage')}
          </Typography>
          <Box
            sx={{
              backgroundColor: 'grey.100',
              borderRadius: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                fontFamily: 'monospace',
              }}
            >
              {t('auth.contactAdministratorEmail')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('auth.contactAdministratorFooter')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseContactDialog}
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginPage;

